import { z } from 'zod'
import { uuid } from './primitives.ts'

/** M24-C: the connector frame (docs/M24.md A, D, E; ADR-125; migration `20261220100200_connectors.sql`, whose header holds every rule). A connector is a
 *  club's setting at a site — which provider's file the server's hourly job reads (a kind of `ref.connector_kind`), for which point or station
 *  (`external_ref`), under which name on screen (`point_label`), switched on or off — and the job's attempts and original files are kept beside it
 *  (`integ.ingestion_run`, `integ.raw_payload`, 90 days à confirmer). Nothing here controls anything, and no host, URL or path is ever data: the
 *  addresses are code (`packages/jobs`, rule 3), so a restored database holds none to call.
 *  - `connector.define@1` (an online intent of the direction, `connector.manage`: never a workstation, no projector, `sendAndWait`; no second factor
 *    and no server nonce since the owner's decision of 2026-10-28, `20261228100000_forecast_settings_no_code.sql`): a new connector at the command's
 *    site, DISABLED; one open connector per site and kind.
 *  - `connector.update@1` (the same): turn on or off, rename, change the staleness or the note (null clears), archive (true only; an archived connector
 *    never changes again). At least one field besides the id and the revision.
 *  Each schema mirrors `core.command_type.payload_schema`; `npm run test:contracts` runs the same fixtures and random payloads through both. */

/** The two directions of a connector kind: a forecast (M24) or a station's readings (part S, designed). */
export const CONNECTOR_DIRECTIONS = ['forecast', 'station'] as const
export type ConnectorDirection = (typeof CONNECTOR_DIRECTIONS)[number]
/** How an attempt of the job ended (`integ.ingestion_run.outcome`). */
export const INGESTION_OUTCOMES = ['ok', 'duplicate', 'not_modified', 'unchanged', 'refused', 'failed'] as const
export type IngestionOutcome = (typeof INGESTION_OUTCOMES)[number]
/** A provider's Retry-After is honoured up to this many hours after the run's start, never more (a bug in the job can never park a connector): the
 *  table's CHECK, the job's cap, and the figure Réglages prints beside the runs ("24 h au plus"). */
export const RETRY_AFTER_MAX_HOURS = 24
/** The largest original file kept (and read while downloading: the job stops counting past it). */
export const RAW_PAYLOAD_MAX_BYTES = 1024 * 1024
/** The job's whole request, its body included. */
export const CONNECTOR_HTTP_TIMEOUT_MS = 30_000
/** The staleness a club may set: whole hours, one hour to seven days. */
export const STALE_AFTER_HOURS_MIN = 1
export const STALE_AFTER_HOURS_MAX = 168
export const POINT_LABEL_MAX = 60
export const CONNECTOR_NOTE_MAX = 300
/** A kind's code (`ref.connector_kind.code`). */
export const connectorKindCode = z.string().regex(/^[a-z][a-z0-9_]{2,40}$/)
/** A point's or a station's code at its provider: letters, digits, `_` and `-` only (it is put in a path by the job, after its kind's own pattern). */
export const EXTERNAL_REF_PATTERN = /^[A-Za-z0-9_-]{1,64}$/
export const externalRef = z.string().regex(EXTERNAL_REF_PATTERN)

const staleAfterHours = z.number().int().min(STALE_AFTER_HOURS_MIN).max(STALE_AFTER_HOURS_MAX)
const pointLabel = z.string().min(1).max(POINT_LABEL_MAX)
const connectorNote = z.string().max(CONNECTOR_NOTE_MAX).nullable()

export const connectorDefineV1 = z.strictObject({
  connector_id: uuid,
  site_id: uuid,
  kind: connectorKindCode,
  external_ref: externalRef,
  point_label: pointLabel,
  stale_after_hours: staleAfterHours.optional(),
  note: connectorNote.optional(),
})
export const connectorUpdateV1 = z.strictObject({
  connector_id: uuid,
  expected_revision: z.number().int().min(1),
  enabled: z.boolean().optional(),
  point_label: pointLabel.optional(),
  stale_after_hours: staleAfterHours.optional(),
  note: connectorNote.optional(),
  archive: z.literal(true).optional(),
}).refine((o) => Object.keys(o).length >= 3, 'at least one field besides the connector and its revision')

export type ConnectorDefineV1 = z.infer<typeof connectorDefineV1>
export type ConnectorUpdateV1 = z.infer<typeof connectorUpdateV1>

/** The counts an attempt records ({elements, steps, values} of the file it read). */
export interface IngestionCounts { elements: number; steps: number; values: number }
/** A run as `api.connector_status` returns it: never a byte, a URL or a validator. */
export interface IngestionRunView {
  run_id: string; started_at: string; finished_at: string; outcome: IngestionOutcome; http_status: number | null; retry_after: string | null
  error_code: string | null; counts: IngestionCounts | null; raw_payload_id: string | null; issue_id: string | null; job_version: string | null
}
/** `integ.v_freshness` for one connector: derived from the runs and the newest issue; a missing issue is "inconnu" (null) and stale, never zero. */
export interface ConnectorFreshness {
  last_ok_at: string | null; last_run_at: string | null; last_outcome: IngestionOutcome | null; last_error_code: string | null
  last_http_status: number | null; retry_after: string | null; newest_issue_at: string | null; stale: boolean
}
export interface ConnectorWords { fr: string; en: string }
/** One connector of the site (archived ones too, with `archived_at`). */
export interface ConnectorView {
  connector_id: string; kind: string; external_ref: string; point_label: string; enabled: boolean; stale_after_hours: number; note: string | null
  archived_at: string | null; revision: number; kind_label: ConnectorWords; provider_name: string; attribution: ConnectorWords
  derived_attribution: ConnectorWords; licence: string | null; licence_url: string | null; has_reader: boolean
  freshness: ConnectorFreshness
  /** the last 20, newest first */
  runs: IngestionRunView[]
}
/** A kind that can be offered (it has a reader). */
export interface ConnectorKindView {
  code: string; direction: ConnectorDirection; label: ConnectorWords; provider_name: string; licence: string | null; licence_url: string | null
  external_ref_pattern: string; default_stale_after_hours: number
}
/** What `api.connector_status(p_org, p_site)` answers (the direction, online; pgTAP 90 pins the keys: change them together). */
export interface ConnectorStatus {
  site_id: string; as_of: string; retry_after_max_hours: number
  /** the club's keep of runs and original files: its `connector_log` policy (an ISO 8601 period, e.g. "P90D") */
  keep: { category: 'connector_log'; period: string; validated: boolean } | null
  kinds: ConnectorKindView[]
  connectors: ConnectorView[]
}
